<template>
	<div @click="event => {event.stopPropagation()}">
		<div class="transactions">
			<v-divider class="mt-2"></v-divider>
			<div class="transactions-container">
				<SwipeActionsContainer
					:items="localVoucher.customerVoucherTransactions"
					:actions="[
						{
							icon: 'mdi-delete',
							type: 'delete',
							color: 'white',
							backgroundColor: 'var(--background-color-alt2)'
						},
						{
							icon: 'mdi-pencil',
							type: 'edit',
							backgroundColor: 'var(--background-color-alt)'
						}
					]"
					@action-click="handleSwipeAction"
				>
					<template #default="{ item: transaction }">
						<v-row class="transaction-row">
							<v-col v-if="transaction.createdDateTimeString != today && transaction.createdDateTimeString != yesterday" class="date-col">
								{{ formattedTransactionDate(transaction.createdDateTime) }}
							</v-col>
							<v-col v-if="transaction.createdDateTimeString == today" class="date-col">Today</v-col>
							<v-col v-if="transaction.createdDateTimeString == yesterday" class="date-col">Yesterday</v-col>
							<v-col class="amount-col">
								<span>{{ formatter.format(transaction.amount) }}</span>
							</v-col>
						</v-row>
					</template>
				</SwipeActionsContainer>
				<v-divider class="mt-5"></v-divider>

				<!-- Original voucher row (non-swipeable) -->
				<v-row class="transaction-row">
					<v-col v-if="localVoucher.createdDateTimeString != today && localVoucher.createdDateTimeString != yesterday" class="date-col">{{ formattedTransactionDate(localVoucher.createdDateTime) }}</v-col>
					<v-col v-if="localVoucher.createdDateTimeString == today" class="date-col">Today</v-col>
					<v-col v-if="localVoucher.createdDateTimeString == yesterday" class="date-col">Yesterday</v-col>
					<v-col class="amount-col">
						<span class="moneyIn">+{{ formatter.format(localVoucher.faceValue) }}</span>
					</v-col>
				</v-row>
			</div>
			
		</div>

		<!-- Edit Transaction Modal -->
		<MGDialog v-model="showEditModal" max-width="400px" title="Enter the amount redeemed" content-height="150px">
			<template #content>
				<v-text-field class="mgTextField"
					v-model="editAmount"
					label="New Amount"
					type="text" 
					inputmode="decimal"
					:rules="[rules.required, rules.positive, rules.maxBalance]"
					autofocus
					prefix="£"
					variant="solo"
					:flat="true"
					clearable
					single-line
					bg-color="white"
					@keydown="_preventNegative($event)"
				/>
				<div class="text-caption">
					Current amount: {{ formatter.format(editingTransaction?.amount || 0) }}
				</div>
			</template>
			<template #actions>
				<MGButton :fullWidth="true" class="button-row" text="Save" @click="saveEdit" :disabled="!editAmount || editAmount <= 0" />
				<MGButton :stacked="true" :fullWidth="true" class="button-row" :outline="true" text="Cancel" @click="cancelEdit" />
			</template>
		</MGDialog>
	</div>
</template>

<script>
import Services from '../../mixins/Services';
import SwipeActionsContainer from './SwipeActionsContainer.vue';
import MGButton from './MGButton.vue';
import MGDialog from './MGDialog.vue';

export default {
	mixins: [Services],
	name: "VoucherTransactionsList",
	components: {
		SwipeActionsContainer,
		MGButton,
		MGDialog
	},
	props: ['search', 'localVoucher'],
	data() {
		return{
			query: "",
			hide: [],
			context: false,
			currentVoucher: this.localVoucher,
			showEditModal: false,
			editAmount: '',
			editingTransaction: null,
			rules: {
				required: value => !!value || 'Amount is required',
				positive: value => value > 0 || 'Amount must be positive',
				maxBalance: value => {
					if (!value) return true;
					const currentBalance = this.localVoucher.currentBalance;
					const oldAmount = this.editingTransaction?.amount || 0;
					const newAmount = parseFloat(value);
					const availableBalance = currentBalance + oldAmount;
					return newAmount <= availableBalance || 'Invalid amount';
				}
			}
		}
	},
    computed: {
        formattedTransactionDate() {
            return (createdDateTime) => {
                const date = new Date(createdDateTime);
                if (isNaN(date)) {
                    return 'Invalid Date';
                }
                const day = String(date.getDate()).padStart(2, '0');
                const month = date.toLocaleString('default', { month: 'long' });
                const year = date.getFullYear();
                return `${day} ${month}, ${year}`;
            };
        }
	},
	watch: {
		localVoucher: {
			handler(newVoucher) {
				this.currentVoucher = newVoucher;
			},
			immediate: true
		}
	},
	methods: {
		onLoad() {
		},
		deleteVoucherTransaction: function(voucher, transaction) {
			this._deleteVoucherTransaction(voucher.id, transaction.id, () => {
				this.$emit("transactionDeleted", transaction);
			});
		},
		handleSwipeAction({ item: transaction, action }) {
			if (action.type === 'delete') {
				this.deleteVoucherTransaction(this.currentVoucher, transaction);
			} else if (action.type === 'edit') {
				this.openEditModal(transaction);
			}
		},
		openEditModal(transaction) {
			this.editingTransaction = transaction;
			this.editAmount = transaction.amount.toString();
			this.showEditModal = true;
		},
		cancelEdit() {
			this.showEditModal = false;
			this.editAmount = '';
			this.editingTransaction = null;
		},
		saveEdit() {
			if (!this.editAmount || this.editAmount <= 0) {
				return;
			}
			const oldAmount = this.editingTransaction?.amount || 0;

			const newAmount = parseFloat(this.editAmount);
			const transaction = this.editingTransaction;

			this._updateVoucherTransaction(this.currentVoucher.id, transaction.id, newAmount, () => {
				transaction.amount = newAmount;
				this.cancelEdit();
				this.$emit("transactionUpdated", transaction, newAmount, oldAmount);
			});
		}
	},
	mounted: function() { this.onLoad(); }
}
</script>

<style scoped>
.icon {
	width: 35px;
	background-color: #E7ECF7;
	border-radius: 90px;
}

.amount-col {
	text-align: right;
	vertical-align: center;
	padding-top: 8px;
	padding-bottom: 0px;
}

.deleteButton {
	margin-left: 5px;
}

.details-col {
	padding-top: 0px;
	padding-bottom: 0px;
	vertical-align: center;
}

.icon-col {
	padding-top: 2px;
	padding-bottom: 0px;
	vertical-align: center;
}

.context-col {
	text-align: right;
	padding-top: 0px;
	padding-bottom: 0px;
	vertical-align: center;
	img {
		opacity: 0.5;
	}
	transition: all .3s fade;
}

.context-button
{
	width: 40px !important;
	padding: 0px !important;
	min-width: 40px !important;
	height: 40px !important;
	margin-left: -1px;
}

.context-close-button
{
	width: 20px !important;
	padding: 0px !important;
	min-width: 20px !important;
	height: 40px !important;
	margin-left: -1px;
}

.date-col {
	font-size: 13px;
	padding-bottom: 5px;
}

.category {
	font-size: 11px;
	color: #555555;
	font-weight: lighter;
}

.moneyIn {
	background-color: #23af6433;
	color: #23AF64;
	padding: 3px;
	border-radius: 3px;
}

.transaction-row {
	min-height: 70px;
	.v-col {
		padding-top: 27px;
	}
}

.transactions-container {
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 0;
  height: 90%;
  min-height: 100px;
}

:deep(.swipe-row) {
	min-height: 10px !important;
}

/* Add margin to the first action button */
:deep(.swipe-actions .action-container:first-child) {
	margin-left: 8px;
}

:deep(.no-uppercase) {
	text-transform: none !important;
}
</style>